import type { AssetRenewal, AssetType } from '@fos/shared/contracts/maintenance';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../../../lib/cn';
import { Menu, MenuContent, MenuRadioGroup, MenuRadioItem, MenuTrigger } from '../../../ui/Menu';
import { Mono } from '../../../ui/Mono';
import { TOUCH_TEXT } from '../../mobile';
import {
  ASSET_TYPE_ICONS,
  amountWithCycle,
  dateText,
  daysLeftText,
  renewalTone,
  TONE_TEXT,
} from '../maintenance-format';

/** Kropka koloru klienta (kolor klienta to dane, nie token; bez koloru: neutralna). */
export function ClientColorDot({ color, className }: { color: string | null | undefined; className?: string }) {
  const style = color ? ({ backgroundColor: color } as CSSProperties) : undefined;
  return (
    <span
      aria-hidden
      style={style}
      className={cn('inline-block size-2 shrink-0 rounded-full', !color && 'bg-line-strong', className)}
    />
  );
}

/** Ikona typu zasobu w kafelku (Figma 08: 32 px, ikona 16 px; karta zasobu: 44 px, ikona 20 px). */
export function AssetTypeIcon({ type, size = 32 }: { type: AssetType; size?: 32 | 44 }) {
  const Icon: LucideIcon = ASSET_TYPE_ICONS[type];
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center bg-surface text-fg-secondary',
        size === 32 ? 'size-8 rounded-lg' : 'size-11 rounded-xl',
      )}
    >
      <Icon size={size === 32 ? 16 : 20} strokeWidth={1.75} />
    </span>
  );
}

/** Termin płatności / odnowienia: data (mono) i pod nią „za 12 dni” w kolorze, gdy blisko albo po terminie. */
export function RenewalDate({ renewal, compact = false }: { renewal: AssetRenewal | null; compact?: boolean }) {
  if (!renewal) return <span className="text-fg-muted">—</span>;
  const tone = renewalTone(renewal);
  const near = tone !== 'neutral';
  return (
    <span className="flex flex-col">
      <Mono className="text-[13px] text-fg">{dateText(renewal.date)}</Mono>
      {near && !compact && (
        <span className={cn('text-caption', TONE_TEXT[tone])}>{daysLeftText(renewal.daysLeft)}</span>
      )}
    </span>
  );
}

/** Kwota roczna albo „w pakiecie” (Figma 08: kolumna „Kwota / rok”, mono). */
export function RenewalAmount({ renewal }: { renewal: AssetRenewal | null }) {
  if (renewal?.inPackage) return <Mono className="text-[13px] text-fg-muted">w pakiecie</Mono>;
  const perYear = renewal?.amountPerYear ?? null;
  if (perYear) return <Mono className="text-[13px] text-fg">{amountWithCycle(perYear, null)}</Mono>;
  if (renewal?.amount) return <Mono className="text-[13px] text-fg">{amountWithCycle(renewal.amount, null)}</Mono>;
  return <span className="text-fg-muted">—</span>;
}

export interface FilterOption {
  value: string;
  label: string;
  leading?: ReactNode;
}

/** Filtr w ramce „Klient: wszyscy ⌄” (Figma 08). Pusta wartość = wszyscy. */
export function FilterMenu({
  label,
  value,
  onValueChange,
  options,
  allLabel = 'wszyscy',
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: FilterOption[];
  allLabel?: string;
}) {
  const current = options.find((o) => o.value === value);
  return (
    <Menu>
      <MenuTrigger
        className={cn(
          'inline-flex h-9 max-w-[260px] shrink-0 items-center gap-1.5 rounded-lg border border-line pr-2.5 pl-3 text-body-s text-fg-secondary max-md:h-11',
          'transition-colors hover:border-line-strong hover:text-fg focus-visible:outline-2 focus-visible:outline-accent',
          'data-[state=open]:border-line-strong',
          // lifting 17.10.2026: aktywny filtr — tło i tekst akcentu (DESIGN „Lifting”)
          current && 'border-transparent bg-accent-subtle text-accent hover:border-transparent hover:text-accent',
        )}
      >
        <span className="truncate">
          {label}: {current ? current.label : allLabel}
        </span>
        <ChevronDown size={14} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
      </MenuTrigger>
      <MenuContent align="end" className="max-h-80 overflow-y-auto">
        <MenuRadioGroup value={value} onValueChange={onValueChange}>
          <MenuRadioItem value="">{allLabel.charAt(0).toUpperCase() + allLabel.slice(1)}</MenuRadioItem>
          {options.map((o) => (
            <MenuRadioItem key={o.value} value={o.value}>
              <span className="flex items-center gap-2">
                {o.leading}
                <span className="truncate">{o.label}</span>
              </span>
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}

/** Nagłówek sekcji karty zasobu (Body/S semibold) z akcją po prawej (caption, akcent). */
export function CardSectionHeader({ title, action }: { title: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex min-h-[27px] items-start justify-between gap-3">
      <h2 className="text-body-s font-semibold text-fg">{title}</h2>
      {action}
    </div>
  );
}

/** Link-akcja w nagłówku sekcji („Zmień”, „Dodaj dostęp”). */
export function SectionAction({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'rounded-xs text-caption font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-50',
        TOUCH_TEXT,
      )}
    >
      {children}
    </button>
  );
}
